/**
 * SSE Hub - Semantic subscription layer over SSEClient.
 *
 * Provides:
 * - Typed topic subscriptions with auto-cleanup
 * - Dafne-specific topic helpers
 * - Event routing by topic
 *
 * Usage:
 * ```ts
 * const hub = SseHub.instance;
 *
 * // Subscribe to user-specific events
 * const unsub = hub.subscribeUser(userId, (evt) => {
 *   if (evt.data.eventName === 'sync.data-changed') {
 *     // Handle sync notification
 *   }
 * });
 *
 * // Cleanup on unmount
 * unsub();
 * ```
 */

import { SSEClient, EventSourceHandler } from "./sse-client";

/**
 * Base interface for topic-targeted events (matches server TopicComparableEvent).
 */
export interface TopicComparableEventBase {
  eventName: string;
  topic: string;
  emittedUtc: string;
}

/**
 * Hub event wrapper with receive metadata.
 */
export interface HubEvent<
  T extends TopicComparableEventBase = TopicComparableEventBase,
> {
  /** Client-side epoch ms when event was received */
  receivedAt: number;
  /** The raw server event data */
  data: T;
}

export type HubEventHandler<
  T extends TopicComparableEventBase = TopicComparableEventBase,
> = (evt: HubEvent<T>) => void;

/**
 * Subscription record for auto-cleanup housekeeping.
 */
interface SubRecord {
  topic: string;
  handler: HubEventHandler<TopicComparableEventBase>;
}

// ============================================================
// Topic Helpers (Dafne-specific)
// ============================================================

/**
 * Topic string builders for Dafne.
 * Centralizes topic naming conventions for consistency.
 *
 * Topic Hierarchy:
 * - patient:{id}          - All patient data (entries, CGM, notes from clinicians)
 * - patient:{id}:support  - Support request communications for patient
 * - clinic:{id}           - Clinic-wide events (new support requests, urgent alerts)
 * - user:{id}             - User-specific (settings, relationship requests)
 * - supporter:{id}        - Events about people you're supporting
 */
export const hubTopics = {
  /**
   * Patient data topic - subscribe to receive all data changes for a patient.
   * Use cases:
   * - Multi-device sync (patient's own devices)
   * - Monitor dashboard (family/HCP watching patient)
   * - Real-time updates when entries/CGM data added
   */
  patient: (patientId: string) =>
    `patient:${(patientId || "").trim().toLowerCase()}`,

  /**
   * Patient support topic - support request communications.
   * Use cases:
   * - Patient receives clinician responses
   * - Patient sees status updates on their requests
   */
  patientSupport: (patientId: string) =>
    `patient:${(patientId || "").trim().toLowerCase()}:support`,

  /**
   * Clinic topic - clinic-wide notifications.
   * Use cases:
   * - Clinician dashboard sees new support requests
   * - Urgent patient alerts
   * - Clinic-wide announcements
   */
  clinic: (clinicGroupId: string) =>
    `clinic:${(clinicGroupId || "").trim().toLowerCase()}`,

  /**
   * User-specific topic for user-targeted events.
   * Use cases:
   * - Settings changed on another device
   * - Relationship requests received
   * - Sync notifications
   */
  user: (userId: string) => `user:${(userId || "").trim().toLowerCase()}`,

  /**
   * Supporter topic - events about people you support.
   * Use cases:
   * - Monitor/family member dashboard
   * - HCP patient list updates
   */
  supporter: (supporterId: string) =>
    `supporter:${(supporterId || "").trim().toLowerCase()}`,

  /**
   * Global broadcast topic for system-wide announcements.
   * Use cases:
   * - Maintenance notifications
   * - Feature announcements
   */
  global: () => "global",
};

// ============================================================
// Event Type Discriminators
// ============================================================

/** Sync-related event types */
export type SyncEventType = "sync.data-changed" | "sync.conflict-detected";

/** User event types */
export type UserEventType = "user.settings-changed" | "user.profile-updated";

/** Relationship event types */
export type RelationshipEventType =
  | "relationship.changed"
  | "relationship.request-received"
  | "relationship.request-accepted";

// ============================================================
// SSE Hub Class
// ============================================================

export class SseHub {
  private static _instance: SseHub | undefined;

  /** Get initialized hub instance (throws if not initialized) */
  static get instance(): SseHub {
    if (!this._instance) {
      throw new Error(
        "SseHub not initialized. Call SseHub.initialize() after SSEClient.initialize().",
      );
    }
    return this._instance;
  }

  /** Initialize the singleton instance */
  static initialize(): SseHub {
    if (this._instance) {
      console.warn("SseHub already initialized");
      return this._instance;
    }
    this._instance = new SseHub();
    return this._instance;
  }

  /** Check if initialized */
  static get isInitialized(): boolean {
    return !!this._instance;
  }

  private readonly sse: SSEClient;
  private readonly subs = new Map<
    string,
    Set<HubEventHandler<TopicComparableEventBase>>
  >();
  private readonly allSubRecords: SubRecord[] = [];
  private initialized = false;
  private boundAllHandler?: EventSourceHandler;

  private constructor() {
    this.sse = SSEClient.instance;
  }

  /** Ensure we have a single global handler installed exactly once */
  private ensureInitialized(): void {
    if (this.initialized) return;
    this.boundAllHandler = (raw, _eventName) => this.routeRaw(raw);
    this.sse.addEventListener("", this.boundAllHandler);
    this.initialized = true;
  }

  // ============================================================
  // Generic Topic Subscription
  // ============================================================

  /**
   * Subscribe to events on a specific topic.
   * @param topic Topic string to subscribe to
   * @param handler Event handler
   * @returns Unsubscribe function
   */
  subscribeTopic<T extends TopicComparableEventBase = TopicComparableEventBase>(
    topic: string,
    handler: HubEventHandler<T>,
  ): () => void {
    const trimmed = topic.trim();
    if (!trimmed) throw new Error("Topic required");

    this.ensureInitialized();

    let set = this.subs.get(trimmed);
    if (!set) {
      set = new Set<HubEventHandler<TopicComparableEventBase>>();
      this.subs.set(trimmed, set);
      this.sse.subscribeTopic(trimmed);

      if ((globalThis as any).__SSE_DEBUG__) {
        console.log("[sse-hub] network subscribe", { topic: trimmed });
      }
    }

    set.add(handler as HubEventHandler<TopicComparableEventBase>);
    const rec: SubRecord = {
      topic: trimmed,
      handler: handler as HubEventHandler<TopicComparableEventBase>,
    };
    this.allSubRecords.push(rec);

    return () => this.unsubscribe(rec);
  }

  // ============================================================
  // Dafne-specific Subscription Helpers
  // ============================================================

  /**
   * Subscribe to user-specific events (sync changes, settings, etc.).
   */
  subscribeUser<T extends TopicComparableEventBase = TopicComparableEventBase>(
    userId: string,
    handler: HubEventHandler<T>,
  ): () => void {
    return this.subscribeTopic(hubTopics.user(userId), handler);
  }

  /**
   * Subscribe to supporter events (updates about supported users).
   */
  subscribeSupporter<
    T extends TopicComparableEventBase = TopicComparableEventBase,
  >(supporterId: string, handler: HubEventHandler<T>): () => void {
    return this.subscribeTopic(hubTopics.supporter(supporterId), handler);
  }

  /**
   * Subscribe to clinic events (support requests, urgent alerts).
   */
  subscribeClinic<
    T extends TopicComparableEventBase = TopicComparableEventBase,
  >(clinicGroupId: string, handler: HubEventHandler<T>): () => void {
    return this.subscribeTopic(hubTopics.clinic(clinicGroupId), handler);
  }

  /**
   * Subscribe to patient data events (entries, CGM, notes).
   */
  subscribePatient<
    T extends TopicComparableEventBase = TopicComparableEventBase,
  >(patientId: string, handler: HubEventHandler<T>): () => void {
    return this.subscribeTopic(hubTopics.patient(patientId), handler);
  }

  /**
   * Subscribe to patient support events (request updates, responses).
   */
  subscribePatientSupport<
    T extends TopicComparableEventBase = TopicComparableEventBase,
  >(patientId: string, handler: HubEventHandler<T>): () => void {
    return this.subscribeTopic(hubTopics.patientSupport(patientId), handler);
  }

  /**
   * Subscribe to global broadcast events.
   */
  subscribeGlobal<
    T extends TopicComparableEventBase = TopicComparableEventBase,
  >(handler: HubEventHandler<T>): () => void {
    return this.subscribeTopic(hubTopics.global(), handler);
  }

  // ============================================================
  // Internals
  // ============================================================

  /** Unsubscribe one record */
  private unsubscribe(rec: SubRecord): void {
    const set = this.subs.get(rec.topic);
    if (!set) return;

    set.delete(rec.handler);

    // Remove from master list
    const idx = this.allSubRecords.indexOf(rec);
    if (idx >= 0) this.allSubRecords.splice(idx, 1);

    // If no handlers left, unsubscribe from network
    if (set.size === 0) {
      this.subs.delete(rec.topic);
      this.sse.unsubscribeTopic(rec.topic);

      if ((globalThis as any).__SSE_DEBUG__) {
        console.log("[sse-hub] network unsubscribe (no handlers)", {
          topic: rec.topic,
        });
      }
    }
  }

  /** Route raw server event to subscribers */
  private routeRaw(raw: unknown): void {
    if (!raw || typeof raw !== "object") return;

    const obj = raw as TopicComparableEventBase;
    const topic = obj.topic;
    const name = obj.eventName;

    if (typeof topic !== "string" || !topic) return;
    if (typeof name !== "string" || !name) return;

    const normalizedTopic = topic.trim().toLowerCase();
    const handlers = this.subs.get(normalizedTopic);

    if (!handlers?.size) return;

    const evt: HubEvent = { receivedAt: Date.now(), data: obj };

    handlers.forEach((h) => {
      try {
        h(evt);
      } catch (err) {
        console.warn("[sse-hub] Handler error", err);
      }
    });

    if ((globalThis as any).__SSE_DEBUG__) {
      console.log("[sse-hub] routed event", {
        topic: normalizedTopic,
        eventName: name,
      });
    }
  }

  /** Get summary of current subscriptions (for debugging) */
  getSubscriptionSummary(): {
    topics: string[];
    handlerCounts: Record<string, number>;
  } {
    const handlerCounts: Record<string, number> = {};
    for (const [topic, handlers] of this.subs.entries()) {
      handlerCounts[topic] = handlers.size;
    }
    return {
      topics: Array.from(this.subs.keys()).sort(),
      handlerCounts,
    };
  }
}
